import React from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './Select';
import ResellerSearch from './ResellerSearch';
import { Search, X, Filter, Clock } from 'lucide-react';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  searchPlaceholder?: string;
  statusFilter?: string;
  onStatusFilterChange?: (value: string) => void;
  statusOptions?: FilterOption[];
  packageFilter?: string;
  onPackageFilterChange?: (value: string) => void;
  packageOptions?: FilterOption[];
  packageFilterLabel?: string;
  trialFilter?: string;
  onTrialFilterChange?: (value: string) => void;
  trialOptions?: FilterOption[];
  systemFilter?: string;
  onSystemFilterChange?: (value: string) => void;
  systemOptions?: FilterOption[];
  resellerFilter?: string;
  onResellerFilterChange?: (value: string) => void;
  resellerOptions?: FilterOption[];
  expiringFilter?: string;
  onExpiringFilterChange?: (value: string) => void;
  expiringOptions?: FilterOption[];
  resellerNotesFilter?: string;
  onResellerNotesFilterChange?: (value: string) => void;
  resellerNotesOptions?: FilterOption[];
  onClearFilters?: () => void;
  showAdvancedFilters?: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = 'Buscar por nome, WhatsApp, email ou notas do revendedor...',
  statusFilter,
  onStatusFilterChange,
  statusOptions = [],
  packageFilter,
  onPackageFilterChange,
  packageOptions = [],
  packageFilterLabel = 'Importante',
  trialFilter,
  onTrialFilterChange,
  trialOptions = [],
  systemFilter,
  onSystemFilterChange,
  systemOptions = [],
  resellerFilter,
  onResellerFilterChange,
  resellerOptions = [],
  expiringFilter,
  onExpiringFilterChange,
  expiringOptions = [],
  onClearFilters,
  showAdvancedFilters = false
}) => {
  const hasActiveFilters = !!(statusFilter || packageFilter || trialFilter || systemFilter || resellerFilter || expiringFilter);

  // Contar filtros ativos para badge
  const activeFilterCount = [statusFilter, packageFilter, trialFilter, systemFilter, resellerFilter, expiringFilter]
    .filter(Boolean).length;

  // Atalhos rápidos de expiração (subconjunto das opções)
  const quickExpiringShortcuts = [
    { value: 'expired', label: 'Expirados', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' },
    { value: '7',       label: '7 dias',    color: 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200' },
    { value: '15',      label: '15 dias',   color: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200' },
    { value: '30',      label: '30 dias',   color: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200' },
    { value: 'all_asc', label: '↑ Mais próximos', color: 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200' },
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="space-y-4">
        {/* Busca */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <form onSubmit={(e) => { e.preventDefault(); onSearchSubmit(); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className=" absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
              <Button type="submit" variant="default" className="h-11 px-6">
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </form>
          </div>
        </div>

        {/* Atalhos rápidos de expiração — sempre visíveis quando há opções */}
        {expiringOptions.length > 0 && onExpiringFilterChange && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-xs font-medium text-gray-500 shrink-0">
              <Clock className="h-3.5 w-3.5" />
              Vencimento:
            </span>
            {quickExpiringShortcuts.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => onExpiringFilterChange(expiringFilter === s.value ? '' : s.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                  expiringFilter === s.value
                    ? s.color + ' ring-2 ring-offset-1 ring-current font-bold'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {s.label}
              </button>
            ))}
            {expiringFilter && (
              <button
                type="button"
                onClick={() => onExpiringFilterChange('')}
                className="px-2 py-1 rounded-full text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 border border-transparent"
                title="Limpar filtro de vencimento"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Filtros avançados */}
        {showAdvancedFilters && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter className="h-4 w-4" />
              Filtros Avançados
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white text-xs font-bold">
                  {activeFilterCount}
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Filtro de Status */}
              {statusOptions.length > 0 && onStatusFilterChange && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Status</label>
                  <Select
                    value={statusFilter || '__all__'}
                    onValueChange={(v) => onStatusFilterChange(v === '__all__' ? '' : v)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Filtro de Pacote */}
              {packageOptions.length > 0 && onPackageFilterChange && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">{packageFilterLabel}</label>
                  <Select
                    value={packageFilter || '__all__'}
                    onValueChange={(v) => onPackageFilterChange(v === '__all__' ? '' : v)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Todos os pacotes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {packageOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Filtro de Trial */}
              {trialOptions.length > 0 && onTrialFilterChange && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Tipo</label>
                  <Select
                    value={trialFilter || ''}
                    onValueChange={onTrialFilterChange}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Todos os tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {trialOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Filtro de Sistema */}
              {systemOptions.length > 0 && onSystemFilterChange && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Sistema</label>
                  <Select
                    value={systemFilter || ''}
                    onValueChange={onSystemFilterChange}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Todos os sistemas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {systemOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Filtro de Revendedor */}
              {onResellerFilterChange && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Revendedor</label>
                  <ResellerSearch
                    value={resellerFilter || ''}
                    onValueChange={onResellerFilterChange}
                    placeholder="Buscar revendedor..."
                    className="h-10"
                  />
                </div>
              )}

              {/* Filtro de Expiração */}
              {expiringOptions.length > 0 && onExpiringFilterChange && (
                <div className="space-y-1">
                  <label className={`text-xs font-medium flex items-center gap-1 ${expiringFilter ? 'text-orange-600' : 'text-gray-600'}`}>
                    <Clock className="h-3 w-3" />
                    Expiração
                    {expiringFilter && <span className="ml-1 w-2 h-2 rounded-full bg-orange-500 inline-block" />}
                  </label>
                  <Select
                    value={expiringFilter || ''}
                    onValueChange={onExpiringFilterChange}
                  >
                    <SelectTrigger className={`h-10 ${expiringFilter ? 'border-orange-400 ring-1 ring-orange-300' : ''}`}>
                      <SelectValue placeholder="Todos os vencimentos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos os vencimentos</SelectItem>
                      {expiringOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Limpar filtros */}
            {hasActiveFilters && onClearFilters && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={onClearFilters}
                  className="flex items-center gap-2 h-10"
                >
                  <X className="h-4 w-4" />
                  Limpar Filtros
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;